import { Container, Sprite, Texture, TextureSource, Rectangle } from 'pixi.js';
import { KnyttArea } from '../engine/KnyttArea';
import { KnyttPoint } from '../engine/KnyttPoint';
import { PowerNames } from '../engine/JuniValues';
import { bgrToRgb, clamp, moveTowards } from '../engine/KnyttUtil';
import { TILE_SIZE } from '../render/Tileset';
import type { Game } from './Game';
import { IdleState, JumpState, JuniState } from './JuniState';

/** Tuned physics constants, ported verbatim from Juni.cs. */
export const C = {
  JUNI_WIDTH: 10.4,
  JUNI_HEIGHT: 16.4,
  JUMP_SPEED_HIGH: -241,
  JUMP_SPEED_LOW: -235,
  JUMP_SPEED_UMBRELLA: -220,
  GRAVITY: 1125,
  LOW_JUMP_HOLD_POWER: 125,
  HIGH_JUMP_HOLD_POWER: 550,
  DOUBLE_JUMP_BUMP_PX: -2,
  JUST_CLIMBED_TIME: 0.085,
  FREE_JUMP_TIME: 0.102,
  MAX_SPEED_WALK: 90,
  MAX_SPEED_RUN: 175,
  MAX_X_SPEED_UMBRELLA: 130,
  PULL_OVER_FORCE_X: 80,
  PULL_OVER_SPEED_Y: -125,
  UPDRAFT_FORCE: 0.116,
  UPDRAFT_FORCE_HOLD: 0.4,
  MAX_UPDRAFT_SPEED: -225,
  MAX_UPDRAFT_SPEED_HOLD: -233,
  MAX_X_MOVING_DELTA: 2500,
  MAX_X_DECAY_DELTA: 1500,
  TERM_VEL: 350,
  TERM_VEL_UMB: 62.5,
  TERM_VEL_UMB_HIGHJUMP_HOLD: 51.5,
  TERM_VEL_UMB_LOWJUMP_HOLD: 60,
  TERM_VEL_UP: 21,
  CLIMB_SPEED: -125,
  SLIDE_SPEED: 25,
  CLIMB_JUMP_X_SPEED: 125,
  BUMP_Y_SPEED_PX: -4.1,
  GROUND_CONTACT_PX: 5,
  // swim
  SWIM_JUMP_SPEED: -100,
  SWIM_MAX_SPEED_WALK: 50.7,
  SWIM_MAX_SPEED_RUN: 100,
  SWIM_LOW_JUMP_HOLD_POWER: 950,
  SWIM_HIGH_JUMP_HOLD_POWER: 1025,
  SWIM_PULL_OVER_SPEED_Y: -100,
  SWIM_MAX_X_SPEED_UMBRELLA: 55,
  SWIM_TERM_VEL: 100,
  SWIM_TERM_VEL_UMB: 22.9,
  SWIM_TERM_VEL_UMB_HIGHJUMP_HOLD: 9.84,
  SWIM_TERM_VEL_UMB_LOWJUMP_HOLD: 11.7,
  SWIM_UPDRAFT_FORCE: 0.4,
  SWIM_MAX_UPDRAFT_SPEED: -100,
  SWIM_SPRING_SPEED: -100,
  SWIM_EXIT_BOOST: 2.1,
  SPRING_SPEED: -342.7,
  SPRING_SPEED_HIGHJUMP_HOLD: -335.6,
};

const HALF_W = C.JUNI_WIDTH / 2;
const HALF_H = C.JUNI_HEIGHT / 2;
const STEP_UP = 3; // max pixels Juni steps up over a small bump/slope

// Juni.cs setupShape: MainShape sits at (6 − w/2, 8.51 − h/2) = (0.8, 0.31) local,
// and the whole body mirrors with facing (Scale.x = −1) → the box shifts to (−0.8, 0.31)
// facing left. Player.x/y is the Godot node origin; the physics box hangs off it.
const BOX_OFF_X = 0.8;
const BOX_OFF_Y = 0.31;

// Juni.shader palette swap: pixels within ±0.06 of these are replaced by the world's
// [World] Clothes / Skin colours (vec4(.937,…) / vec4(.82,.72,.6) × 255).
const CLOTHES_KEY = [239, 239, 239];
const SKIN_KEY = [209, 184, 153];
const RECOLOR_TOLERANCE = 0.06 * 255;

// Animation table: name -> { frames, step(sec), loop }
interface AnimDef {
  frames: number[];
  step: number;
  loop: boolean;
  next?: string;
}
const ANIMS: Record<string, AnimDef> = {
  Idle: { frames: [47], step: 1, loop: true },
  Walk: { frames: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], step: 0.05, loop: true },
  Run: { frames: [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21], step: 0.03, loop: true },
  Climbing: { frames: [22, 23, 24, 25, 26, 27, 28], step: 0.04, loop: true },
  Sliding: { frames: [29], step: 1, loop: true },
  StartFall: { frames: [30, 31, 32, 33], step: 0.05, loop: false, next: 'Falling' },
  Falling: { frames: [34, 35, 36, 37, 38, 39, 40, 41], step: 0.05, loop: true },
  Jump: { frames: [42, 43, 44, 45, 46], step: 0.075, loop: false },
};

export class Player {
  readonly container = new Container();
  private sprite!: Sprite;
  private frameTextures: Texture[] = [];

  x = 0; // global position (centre-ish, matches GlobalPosition)
  y = 0;
  vx = 0;
  vy = 0;
  dir = 0;
  facingRight = true;
  grounded = false;
  jumps = 0;
  dead = false;
  stopMotion = false; // Shift Hide: freeze physics during a delayed shift

  // Timers
  private justReset = 1; // Juni.cs _Ready: skip the first frame
  justClimbedTime = 0;
  climbedWithPullOver = false;
  canFreeJumpTime = 0;

  // Zone counters
  swimZones = 0;
  noJumps = 0;
  stickies = 0;
  updrafts = 0;
  muffleZones = 0;

  /** Boost the exit-jump when leaving the last swim zone mid-jump (Juni.cs Swim setter). */
  onSwimZoneExit(): void {
    if (this.swimZones > 0 || this.game.areaSwim || this.vy >= 0 || this.state.name !== 'Jump')
      return;
    const speedLimit = this.power(PowerNames.HighJump) ? C.JUMP_SPEED_HIGH : C.JUMP_SPEED_LOW;
    this.vy = Math.max(speedLimit, this.vy * C.SWIM_EXIT_BOOST);
  }
  noClimbZones = 0;
  // Hologram-place gating (AllowHologram): when an area has AllowHologram objects,
  // the hologram may only be deployed while standing in one of them.
  allowHologramZones = 0; // area restricts hologram to designated places
  hologramPlaces = 0; // Juni is currently inside a designated place

  // Custom character (Juni.cs changeCharacter). A custom umbrella sheet disables the
  // canopy bullet shield (Umbrella.Custom → shape disabled).
  private character = 'juni';
  private umbrellaCustom = false;

  // Umbrella: visible only while deployed; deploy frames 0-7, stow 8-15.
  private _umbrellaDeployed = false;
  private umbrellaSprite!: Sprite;
  private umbrellaFrames: Texture[] = [];
  private umbrellaAnim: 'none' | 'deploy' | 'open' | 'stow' = 'none';
  private umbrellaAnimTime = 0;
  private umbrellaAnimIndex = 0;

  get umbrellaDeployed(): boolean {
    return this._umbrellaDeployed;
  }
  set umbrellaDeployed(v: boolean) {
    if (v === this._umbrellaDeployed) return;
    this._umbrellaDeployed = v;
    this.umbrellaAnimTime = 0;
    this.umbrellaAnimIndex = 0;
    if (v) {
      this.umbrellaAnim = 'deploy';
      if (this.umbrellaSprite) this.umbrellaSprite.visible = true;
      this.game.audio.sfx('Umbrella A');
    } else {
      this.umbrellaAnim = 'stow';
      this.game.audio.sfx('Umbrella B');
    }
  }

  // Attachment ("torch") sprite: a centered child at Juni's origin (Juni.tscn
  // AttachmentSprite), equipped via area `Attach` extra-data and persisted in the save.
  private attachmentSprite: Sprite | null = null;

  // Hologram: a frozen decoy at the deploy spot; Juni fades to 45% and enemies
  // target the decoy's "apparent" position.
  private hologramSprite: Sprite | null = null;
  hologramActive = false;
  // Down-button hint (Juni.cs OnShift/OnPlatform + showShiftHint, default-on
  // "Down Button Hint" setting): a small arrow under Juni while she stands in a
  // non-touch Shift / paged Sign (white) or on a TwoWayPlatform (greenish = can
  // also drop through with Down+Jump).
  onShift = 0;
  onPlatform = 0;
  private shiftHintSprite: Sprite | null = null;
  private hologramX = 0;
  private hologramY = 0;
  private holoCooldown = 0;
  private doubleDownTimer = 0;

  // EnemyDetector: a glow whose intensity tracks the strongest nearby reporter
  // (organic enemy → red, SecretDetector → green). The max reporter sets the colour.
  private detectorSprite: Sprite | null = null;
  detectorReverse = 0;
  private detectorColor = 0xff0000;
  private static readonly DETECTOR_RANGE = 170;

  state: JuniState;
  nextState: JuniState | null = null;

  // Ported from Juni.cs's `Jumped` signal (BouncerEnemy/SmartWalkingBouncer listen
  // for it to react to Juni jumping nearby). `real` = no hologram decoy is deployed.
  private jumpListeners: Array<(real: boolean) => void> = [];
  onJump(cb: (real: boolean) => void): () => void {
    this.jumpListeners.push(cb);
    return () => {
      this.jumpListeners = this.jumpListeners.filter((l) => l !== cb);
    };
  }

  // animation
  private animName = 'Idle';
  private animTime = 0;
  private animIndex = 0;

  constructor(readonly game: Game) {
    this.state = new IdleState(this);
  }

  /** Slice a 24px-grid sheet row-major (Juni.cs changeCharacter's Hframes×Vframes). */
  private static sliceSheet(tex: Texture): Texture[] {
    const src = tex.source as TextureSource;
    const cols = Math.max(1, Math.floor(src.width / TILE_SIZE));
    const rows = Math.max(1, Math.floor(src.height / TILE_SIZE));
    const out: Texture[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        out.push(
          new Texture({
            source: src,
            frame: new Rectangle(c * TILE_SIZE, r * TILE_SIZE, TILE_SIZE, TILE_SIZE),
          }),
        );
      }
    }
    return out;
  }

  /**
   * Juni.shader recolor: replace the sheet's clothes/skin key colours with the world's
   * [World] Clothes / Skin BGR colours (per-channel |diff| < 0.06, opaque pixels only).
   * Returns the original texture when neither colour is set or the pixels are unreadable.
   */
  private recolorSheet(tex: Texture): Texture {
    const info = this.game.world.info;
    if (info.clothes === -1 && info.skin === -1) return tex;
    const res = tex.source.resource as CanvasImageSource | undefined;
    if (!res) return tex;
    const w = tex.source.width;
    const h = tex.source.height;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return tex;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(res, 0, 0);
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    const clothes = info.clothes !== -1 ? bgrToRgb(info.clothes) : null;
    const skin = info.skin !== -1 ? bgrToRgb(info.skin) : null;
    const matches = (i: number, key: number[]) =>
      Math.abs(d[i] - key[0]) < RECOLOR_TOLERANCE &&
      Math.abs(d[i + 1] - key[1]) < RECOLOR_TOLERANCE &&
      Math.abs(d[i + 2] - key[2]) < RECOLOR_TOLERANCE &&
      255 - d[i + 3] < RECOLOR_TOLERANCE;
    for (let i = 0; i < d.length; i += 4) {
      const c = clothes && matches(i, CLOTHES_KEY) ? clothes : skin && matches(i, SKIN_KEY) ? skin : null;
      if (c) {
        d[i] = c[0];
        d[i + 1] = c[1];
        d[i + 2] = c[2];
        d[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    const out = Texture.from(canvas);
    out.source.scaleMode = 'nearest';
    return out;
  }

  private setBodyFrames(tex: Texture | null): void {
    if (!tex) return;
    this.frameTextures = Player.sliceSheet(tex);
    const def = ANIMS[this.animName] ?? ANIMS.Idle;
    if (this.sprite)
      this.sprite.texture = this.frameTextures[def.frames[this.animIndex]] ?? this.frameTextures[47] ?? Texture.WHITE;
  }

  private setUmbrellaFrames(tex: Texture | null): void {
    if (!tex) return;
    this.umbrellaFrames = Player.sliceSheet(tex);
    if (this.umbrellaSprite) this.umbrellaSprite.texture = this.umbrellaFrames[0] ?? Texture.WHITE;
  }

  async load(): Promise<void> {
    const tex = await this.game.assets.getTexture('juni/juni.png');
    if (tex) this.frameTextures = Player.sliceSheet(this.recolorSheet(tex));
    this.sprite = new Sprite(this.frameTextures[47] ?? Texture.WHITE);
    this.sprite.anchor.set(0.5, 0.5);
    this.sprite.y = -3; // yknytt Sprite node is at (0,-3) relative to the body
    this.container.addChild(this.sprite);
    this.container.zIndex = 8;

    // Umbrella: 16 frames of 24x24 in umbrella_item.png, sits at (0,-3.95).
    const utex = await this.game.assets.getTexture('juni/umbrella_item.png');
    if (utex) this.umbrellaFrames = Player.sliceSheet(utex);
    this.umbrellaSprite = new Sprite(this.umbrellaFrames[0] ?? Texture.WHITE);
    this.umbrellaSprite.anchor.set(0.5, 0.5);
    this.umbrellaSprite.y = -3.95;
    this.umbrellaSprite.visible = false;
    this.container.addChild(this.umbrellaSprite);

    // Enemy-detector red glow (detector.png), rendered over Juni when organic enemies are near.
    const dtex = await this.game.assets.getTexture('juni/detector.png');
    this.detectorSprite = new Sprite(dtex ?? Texture.WHITE);
    this.detectorSprite.anchor.set(0.5, 0.5);
    this.detectorSprite.y = -3;
    this.detectorSprite.tint = 0xff0000;
    this.detectorSprite.blendMode = 'add';
    this.detectorSprite.visible = false;
    this.container.addChild(this.detectorSprite);

    // Attachment sprite (Juni.tscn AttachmentSprite: centered at Juni's origin, hidden).
    this.attachmentSprite = new Sprite(Texture.EMPTY);
    this.attachmentSprite.anchor.set(0.5, 0.5);
    this.attachmentSprite.visible = false;
    this.container.addChild(this.attachmentSprite);

    // Down-button hint arrow (Juni.tscn ShiftHintSprite at (0,14), down_hint.png).
    const htex = await this.game.assets.getTexture('juni/down_hint.png');
    this.shiftHintSprite = new Sprite(htex ?? Texture.WHITE);
    this.shiftHintSprite.anchor.set(0.5, 0.5);
    this.shiftHintSprite.y = 14;
    this.shiftHintSprite.visible = false;
    this.container.addChild(this.shiftHintSprite);

    // Hologram decoy lives in world space (fixed where deployed), not on Juni.
    this.hologramSprite = new Sprite(this.frameTextures[47] ?? Texture.WHITE);
    this.hologramSprite.anchor.set(0.5, 0.5);
    this.hologramSprite.visible = false;
    this.hologramSprite.zIndex = 8;
    this.game.worldLayer.addChild(this.hologramSprite);

    // Juni.initialize: the save's character (set by Shifts) wins over [World] Character.
    const startCharacter = this.game.values.character ?? this.game.world.info.character;
    if (startCharacter) await this.changeCharacter(startCharacter);

    this.state.onEnter();
  }

  /**
   * Juni.cs changeCharacter: swap the body sheet to `Custom Objects/<name>.png` and the
   * umbrella to `u<name>.png` (both 24px grids). A custom umbrella disables the canopy
   * bullet shield (Umbrella.Custom). Missing sheets keep the current art (the C# would
   * render nothing); "juni"/empty restores the defaults incl. the clothes/skin recolor.
   */
  async changeCharacter(name: string | null): Promise<void> {
    const target = !name || name.toLowerCase() === 'juni' ? 'juni' : name;
    if (target === this.character) return;
    this.character = target;
    this.game.values.character = target;
    if (target === 'juni') {
      const body = await this.game.assets.getTexture('juni/juni.png');
      this.setBodyFrames(body ? this.recolorSheet(body) : null);
      this.setUmbrellaFrames(await this.game.assets.getTexture('juni/umbrella_item.png'));
      this.umbrellaCustom = false;
    } else {
      this.setBodyFrames(await this.game.assets.getTexture(`Custom Objects/${target}.png`));
      const umb = await this.game.assets.getTexture(`Custom Objects/u${target}.png`);
      this.umbrellaCustom = !!umb;
      if (umb) this.setUmbrellaFrames(umb);
      else this.setUmbrellaFrames(await this.game.assets.getTexture('juni/umbrella_item.png'));
    }
  }

  /**
   * Equip/clear the attachment sprite (Juni.cs enableAttachment): "true"/"" → the
   * default torch (juni/Attach.png), "false"/null → none, anything else → a world
   * `Custom Objects/<file>` texture. Normalizes values.attachment for the save.
   */
  async setAttachment(attachment: string | null): Promise<void> {
    const a = attachment?.toLowerCase();
    let tex: Texture | null = null;
    if (a === 'true' || a === '') {
      tex = await this.game.assets.getTexture('juni/Attach.png');
      this.game.values.attachment = 'true';
    } else if (a === 'false' || attachment == null) {
      this.game.values.attachment = 'false';
    } else {
      tex = await this.game.assets.getTexture(`Custom Objects/${attachment}`);
      this.game.values.attachment = attachment;
    }
    if (!this.attachmentSprite) return;
    this.attachmentSprite.texture = tex ?? Texture.EMPTY;
    this.attachmentSprite.visible = !!tex;
  }

  // ---- Hologram ----
  get canDeployHologram(): boolean {
    if (this.state.name !== 'Idle' && this.state.name !== 'WalkRun') return false;
    // AllowHologram: in restricted areas, only deployable inside a designated place.
    return !(this.allowHologramZones > 0 && this.hologramPlaces === 0);
  }
  get apparentX(): number {
    return this.hologramActive ? this.hologramX : this.x;
  }
  get apparentY(): number {
    return this.hologramActive ? this.hologramY : this.y;
  }

  private handleHologram(dt: number): void {
    if (this.holoCooldown > 0) this.holoCooldown -= dt;
    if (this.doubleDownTimer > 0) this.doubleDownTimer -= dt;
    const hasHologram = this.power(PowerNames.Hologram);
    const showInfoHeld = this.game.input.isHeld('show_info');
    if (!hasHologram && !showInfoHeld) return;

    // Double-tap Down also toggles the hologram (yknytt DoubleDownTimer).
    let doubleDown = false;
    if (this.game.input.justPressed('down')) {
      if (this.doubleDownTimer > 0) doubleDown = true;
      else this.doubleDownTimer = 0.25;
    }

    if (showInfoHeld && doubleDown) {
      // Q + double-tap Down quicksaves, independent of the Hologram power.
      this.game.saveGame(true);
      this.game.audio.sfx('Save Spot');
    } else if (
      hasHologram &&
      this.holoCooldown <= 0 &&
      (this.game.input.justPressed('hologram') || doubleDown)
    ) {
      this.holoCooldown = 0.25; // Juni.tscn HologramTimer wait_time
      if (!this.hologramActive) {
        if (this.canDeployHologram) this.deployHologram();
      } else {
        this.stopHologram();
      }
    }
  }

  private deployHologram(): void {
    if (!this.hologramSprite) return;
    this.hologramActive = true;
    this.hologramX = this.x;
    this.hologramY = this.y;
    this.hologramSprite.texture = this.sprite.texture;
    this.hologramSprite.scale.x = this.facingRight ? 1 : -1;
    this.hologramSprite.x = Math.round(this.x);
    this.hologramSprite.y = Math.round(this.y - 3);
    this.hologramSprite.visible = true;
    this.game.audio.sfx('Hologram A');
  }

  stopHologram(silent = false): void {
    if (!this.hologramActive) return;
    this.hologramActive = false;
    if (this.hologramSprite) this.hologramSprite.visible = false;
    if (!silent) this.game.audio.sfx('Hologram B');
  }

  // ---- Enemy detector ----
  /**
   * A detector source at (cx,cy) reports itself; the strongest sets the glow + colour.
   * Organic enemies use the default range (170), non-discrete falloff, red. A
   * SecretDetector overrides with a small range (26), discrete (full intensity), green.
   * Mirrors GDKnyttBaseObject.updateOrganicEnemy + Juni.updateOrganicEnemy.
   */
  reportOrganicEnemy(
    cx: number,
    cy: number,
    range = Player.DETECTOR_RANGE,
    discrete = false,
    color = 0xff0000,
  ): void {
    if (!this.power(PowerNames.EnemyDetector)) return;
    const dist = Math.hypot(cx - this.x, cy - this.y);
    if (dist >= range) return;
    const rev = discrete ? 1 : 1 - dist / range;
    if (rev > this.detectorReverse) {
      this.detectorReverse = rev;
      this.detectorColor = color;
    }
  }

  private updateDetector(): void {
    if (!this.detectorSprite) return;
    if (this.detectorReverse > 0) {
      this.detectorSprite.visible = true;
      this.detectorSprite.tint = this.detectorColor;
      this.detectorSprite.alpha = 0.25 + Math.random() * (this.detectorReverse * 0.65);
    } else {
      this.detectorSprite.visible = false;
    }
    this.detectorReverse = 0; // reset; reporters re-report each frame
    this.detectorColor = 0xff0000;
  }

  /** Advance the umbrella deploy/stow animation. */
  private updateUmbrella(dt: number): void {
    if (!this.umbrellaSprite) return;
    this.umbrellaSprite.scale.x = this.facingRight ? 1 : -1;
    if (this.umbrellaAnim === 'none') return;
    if (this.umbrellaAnim === 'open') {
      this.umbrellaSprite.texture = this.umbrellaFrames[7] ?? this.umbrellaSprite.texture;
      return;
    }
    this.umbrellaAnimTime += dt;
    const step = 0.025; // yknytt deploy/stow step
    while (this.umbrellaAnimTime >= step) {
      this.umbrellaAnimTime -= step;
      this.umbrellaAnimIndex++;
      if (this.umbrellaAnim === 'deploy') {
        if (this.umbrellaAnimIndex >= 7) {
          this.umbrellaAnimIndex = 7;
          this.umbrellaAnim = 'open';
        }
        this.umbrellaSprite.texture = this.umbrellaFrames[this.umbrellaAnimIndex] ?? this.umbrellaSprite.texture;
      } else {
        // stow: frames 8..15, then hide
        const f = 8 + this.umbrellaAnimIndex;
        if (f > 15) {
          this.umbrellaAnim = 'none';
          this.umbrellaSprite.visible = false;
          return;
        }
        this.umbrellaSprite.texture = this.umbrellaFrames[f] ?? this.umbrellaSprite.texture;
      }
    }
  }

  moveToTile(area: KnyttArea, tile: KnyttPoint): void {
    const ox = area.position.x * TILE_SIZE * 25;
    const oy = area.position.y * TILE_SIZE * 10;
    this.x = ox + TILE_SIZE * tile.x + TILE_SIZE / 2;
    this.y = oy + TILE_SIZE * tile.y + TILE_SIZE / 2 + 3.4; // BaseCorrection
    this.vx = 0;
    this.vy = 0;
    this.syncSprite();
  }

  /**
   * Juni.moveToPosition (Juni.cs:806-809): place Juni at a tile centre with the
   * BaseCorrection applied *up* (−3.4) when she's moving upward, else down (+3.4).
   * Unlike moveToTile this does NOT touch velocity — a Shift preserves momentum.
   */
  moveToPosition(area: KnyttArea, tile: KnyttPoint, upCorrection = false): void {
    const ox = area.position.x * TILE_SIZE * 25;
    const oy = area.position.y * TILE_SIZE * 10;
    this.x = ox + TILE_SIZE * tile.x + TILE_SIZE / 2;
    this.y = oy + TILE_SIZE * tile.y + TILE_SIZE / 2 + (this.vy < 0 && upCorrection ? -3.4 : 3.4);
    this.syncSprite();
  }

  areaTile(): KnyttPoint {
    const lx = this.x - this.game.areaOriginX;
    const ly = this.y - this.game.areaOriginY;
    return new KnyttPoint(Math.floor(lx / TILE_SIZE), Math.floor(ly / TILE_SIZE));
  }

  // ---- Power / zone helpers ----
  power(p: PowerNames): boolean {
    return this.game.values.getPower(p);
  }
  get swim(): boolean {
    return this.swimZones > 0 || this.game.areaSwim;
  }
  get canJump(): boolean {
    return this.noJumps === 0;
  }
  get sticky(): boolean {
    return this.stickies > 0;
  }
  get inUpdraft(): boolean {
    return this.updrafts > 0;
  }
  get jumpLimit(): number {
    return this.power(PowerNames.DoubleJump) ? 2 : 1;
  }
  get canClimb(): boolean {
    return (
      this.power(PowerNames.Climb) &&
      this.noClimbZones === 0 &&
      this.touchingWall(this.facingRight ? 1 : -1) &&
      // Checkers.Colliding also requires zero overlapping *areas* in the climb strip —
      // Halt/NoClimb zones sit on collision layer 16, which the strip's mask sees.
      !this.climbStripBlocked(this.facingRight ? 1 : -1) &&
      !this.isInside()
    );
  }
  /** The climb-checker strip overlaps a Halt/NoClimb zone (blocks Checkers.Colliding). */
  private climbStripBlocked(d: number): boolean {
    return (
      this.game.objectLayers?.climbBlockedRect(
        this.x + d * 6.23 - 0.2,
        this.y + 0.2 - 4.3,
        0.4,
        8.6,
      ) ?? false
    );
  }
  get canUmbrella(): boolean {
    return this.power(PowerNames.Umbrella);
  }
  get walkRun(): boolean {
    return this.power(PowerNames.Run) && !this.game.input.isHeld('walk');
  }
  get maxSpeed(): number {
    return this.swim
      ? this.walkRun
        ? C.SWIM_MAX_SPEED_RUN
        : C.SWIM_MAX_SPEED_WALK
      : this.walkRun
        ? C.MAX_SPEED_RUN
        : C.MAX_SPEED_WALK;
  }
  get justClimbed(): boolean {
    return this.justClimbedTime > 0;
  }
  set justClimbed(v: boolean) {
    this.justClimbedTime = v ? C.JUST_CLIMBED_TIME : 0;
  }
  get canFreeJump(): boolean {
    return this.canFreeJumpTime > 0;
  }
  set canFreeJump(v: boolean) {
    this.canFreeJumpTime = v ? C.FREE_JUMP_TIME : 0;
  }
  get didAirJump(): boolean {
    return (
      this.game.input.justPressed('jump') &&
      this.canJump &&
      (this.canFreeJump || this.jumps < this.jumpLimit)
    );
  }
  get canAnyJump(): boolean {
    return this.canJump && (this.grounded || this.canFreeJump || this.jumps < this.jumpLimit);
  }

  get moveDirection(): number {
    if (this.sticky) return 0;
    const d = this.game.input.direction;
    if (d !== 0) this.facingRight = d > 0;
    return d;
  }

  get terminalVelocity(): number {
    const input = this.game.input;
    if (this.swim) {
      if (!this.umbrellaDeployed) return C.SWIM_TERM_VEL;
      if (this.inUpdraft) return C.TERM_VEL_UP;
      if (!input.isHeld('jump') && !input.isHeld('umbrella')) return C.SWIM_TERM_VEL_UMB;
      return this.power(PowerNames.HighJump)
        ? C.SWIM_TERM_VEL_UMB_HIGHJUMP_HOLD
        : C.SWIM_TERM_VEL_UMB_LOWJUMP_HOLD;
    }
    if (!this.umbrellaDeployed) return C.TERM_VEL;
    if (this.inUpdraft) return C.TERM_VEL_UP;
    if (!input.isHeld('jump') && !input.isHeld('umbrella')) return C.TERM_VEL_UMB;
    return this.power(PowerNames.HighJump)
      ? C.TERM_VEL_UMB_HIGHJUMP_HOLD
      : C.TERM_VEL_UMB_LOWJUMP_HOLD;
  }

  transitionState(s: JuniState): void {
    this.nextState = s;
  }

  // ---- Main update ----
  update(dt: number): void {
    if (this.dead) return;
    // Shift Hide (Shift.cs:44) freezes Juni's physics during the delay.
    if (this.stopMotion) {
      this.syncSprite();
      return;
    }
    this.processMotion(dt);
    this.updateMotionParticles(dt);
    this.animate(dt);
    this.updateUmbrella(dt);
    this.updateDetector();
    this.syncSprite();
  }

  private motionParticleTimer = 0;
  /**
   * Cloud puffs (JuniMotionParticles): while walking/running/climbing on a Cloud (7,16)
   * tile, kick up cloud puffs from the feet/hands on the motion timer (walk 0.2s,
   * run/climb 0.1s).
   */
  private updateMotionParticles(dt: number): void {
    let interval = 0;
    let px = this.x;
    let py = this.y;
    if (this.state.name === 'WalkRun' && this.grounded) {
      interval = this.walkRun ? 0.1 : 0.2; // run faster than walk
      py = this.y + HALF_H; // ground/foot position
    } else if (this.state.name === 'Climb') {
      interval = 0.1;
    } else {
      this.motionParticleTimer = 0;
      return;
    }
    this.motionParticleTimer -= dt;
    if (this.motionParticleTimer <= 0) {
      this.motionParticleTimer = interval;
      if (this.game.objectLayers?.cloudAt(px, py)) this.game.particles.spawnCloudPuff(px, py);
    }
  }

  private processMotion(dt: number): void {
    const input = this.game.input;

    // Juni.cs just_reset: physics (incl. collision response) skipped for 2 frames
    // after a respawn so a bad landing can settle, 1 frame at first load.
    if (this.justReset > 0) {
      this.justReset--;
      return;
    }

    if (this.nextState) {
      this.state.onExit();
      this.state = this.nextState;
      this.nextState = null;
      this.state.onEnter();
    }

    this.state.preProcess(dt);

    if (this.canUmbrella && input.justPressed('umbrella')) {
      this.umbrellaDeployed = !this.umbrellaDeployed;
    }
    this.handleHologram(dt);

    this.handleXMovement(dt);
    this.handleGravity(dt);
    this.handleBumps(dt);

    this.state.postProcess(dt);

    // Pull-over-edge
    if (this.justClimbed) {
      if (Math.abs(this.justClimbedTime - C.JUST_CLIMBED_TIME) < 1e-6) {
        this.climbedWithPullOver = this.vy < 0;
        if (this.climbedWithPullOver) {
          // Juni.cs:509-519: a ≤4px bump ahead on the first pull-over frame → vy=0
          // (step onto it) instead of the pull-over launch.
          if (this.bump(false)) this.vy = 0;
          else this.vy = Math.max(this.swim ? C.SWIM_PULL_OVER_SPEED_Y : C.PULL_OVER_SPEED_Y, this.vy);
        }
      }
      if (this.climbedWithPullOver) {
        this.vx = this.facingRight
          ? Math.max(this.vx, C.PULL_OVER_FORCE_X)
          : Math.min(this.vx, -C.PULL_OVER_FORCE_X);
      }
      this.justClimbedTime -= dt;
      if (this.canFreeJumpTime <= 0) this.justClimbed = false;
    }

    if (this.canFreeJump) {
      this.canFreeJumpTime -= dt;
      if (this.canFreeJumpTime <= 0) {
        this.jumps++;
        this.canFreeJump = false;
      }
    }

    this.vy = Math.min(this.terminalVelocity, this.vy);

    if (this.isInside()) {
      this.x += -25 * this.moveDirection * dt;
      this.y += (input.isHeld('up') ? 50 : -10) * dt;
    } else {
      this.moveAndCollide(dt);
    }
  }

  private handleXMovement(dt: number): void {
    if (this.dir !== 0) {
      const uspeed = this.umbrellaDeployed
        ? Math.min(this.maxSpeed, this.swim ? C.SWIM_MAX_X_SPEED_UMBRELLA : C.MAX_X_SPEED_UMBRELLA)
        : this.maxSpeed;
      this.vx = moveTowards(this.vx, this.dir * uspeed, C.MAX_X_MOVING_DELTA * dt);
    } else {
      this.vx = moveTowards(this.vx, 0, C.MAX_X_DECAY_DELTA * dt);
    }
  }

  private handleGravity(dt: number): void {
    const input = this.game.input;
    const jumpHeld = input.isHeld('jump') || (this.umbrellaDeployed && input.isHeld('umbrella'));
    if (this.inUpdraft && this.umbrellaDeployed) {
      this.vy -=
        C.GRAVITY *
        dt *
        (this.swim ? C.SWIM_UPDRAFT_FORCE : jumpHeld ? C.UPDRAFT_FORCE_HOLD : C.UPDRAFT_FORCE);
      this.vy = Math.max(
        this.swim ? C.SWIM_MAX_UPDRAFT_SPEED : jumpHeld ? C.MAX_UPDRAFT_SPEED_HOLD : C.MAX_UPDRAFT_SPEED,
        this.vy,
      );
    } else {
      if (!this.grounded) this.vy += C.GRAVITY * dt;
      else if (!(input.justPressed('jump') && this.canAnyJump)) this.vy = C.GRAVITY * dt;
      if (jumpHeld && this.canJump) {
        const jumpHold = this.power(PowerNames.HighJump)
          ? this.swim
            ? C.SWIM_HIGH_JUMP_HOLD_POWER
            : C.HIGH_JUMP_HOLD_POWER
          : this.swim
            ? C.SWIM_LOW_JUMP_HOLD_POWER
            : C.LOW_JUMP_HOLD_POWER;
        this.vy -= jumpHold * dt;
      }
    }
  }

  private handleBumps(_dt: number): void {
    const movingState =
      (this.state.name === 'WalkRun' || this.state.name === 'Jump' || this.state.name === 'Fall') &&
      this.game.input.direction !== 0;
    const pullOver = this.justClimbed && this.climbedWithPullOver;
    // Juni.cs handleBumps: WalkRun (STEEP_SLOPES=1) and pull-over use the 4px Bump
    // checker; air states (Jump/Fall) only the 2px LittleBump.
    const little = !(this.state.name === 'WalkRun' || pullOver);
    if ((movingState || pullOver) && this.bump(little)) {
      // nudge upward over the bump
      const ny = this.y + C.BUMP_Y_SPEED_PX;
      if (!this.overlapsAt(this.x, ny)) this.y = ny;
    }
  }

  executeJump(jumpSpeed?: number, airJump = false, resetJumps = false, sound = true): void {
    if (jumpSpeed === undefined) {
      jumpSpeed = this.swim
        ? C.SWIM_JUMP_SPEED
        : this.umbrellaDeployed
          ? C.JUMP_SPEED_UMBRELLA
          : this.power(PowerNames.HighJump)
            ? C.JUMP_SPEED_HIGH
            : C.JUMP_SPEED_LOW;
    }
    this.transitionState(new JumpState(this));
    this.playAnim(jumpSpeed < 0 ? 'Jump' : 'StartFall');
    this.vy = jumpSpeed;
    if (airJump && this.jumps > 0) {
      // Small positional nudge + one-shot burst on air-jump (Juni.cs executeJump).
      const nudgeX = jumpSpeed !== 0 ? (C.DOUBLE_JUMP_BUMP_PX * this.vx) / jumpSpeed : 0;
      const ny = this.y + C.DOUBLE_JUMP_BUMP_PX;
      const nx = this.x + nudgeX;
      if (!this.overlapsAt(nx, ny)) {
        this.x = nx;
        this.y = ny;
      }
      this.game.doubleJumpEffect.play(this.x, this.y);
      // Juni.cs doMuffle: the double-jump player is NOT in the muffled set.
      if (sound) this.game.audio.sfx('Knytt Double Jump');
    } else if (!airJump && sound) {
      this.game.audio.sfx('Knytt Jump (troupe)', true);
    }
    this.jumps = resetJumps ? 1 : this.jumps + 1;
    this.justClimbed = false;
    this.canFreeJump = false;
    for (const cb of this.jumpListeners) cb(!this.hologramActive);
  }

  // ---- Collision ----
  /** X offset of the physics box centre from the origin — mirrors with facing. */
  private get boxOffX(): number {
    return this.facingRight ? BOX_OFF_X : -BOX_OFF_X;
  }
  /** Physics-box AABB for an origin (cx,cy) (MainShape at (±0.8, 0.31) local). */
  private overlapsAt(cx: number, cy: number): boolean {
    return this.game.collision.boxOverlaps(
      cx + this.boxOffX - HALF_W,
      cy + BOX_OFF_Y - HALF_H,
      C.JUNI_WIDTH,
      C.JUNI_HEIGHT,
    );
  }
  /**
   * Juni.tscn InsideDetector: a small 3.8×10 inner box at local (0,2) — "inside solid"
   * (→ the inside-drift escape) only when embedded past what push-out can handle.
   */
  isInside(): boolean {
    return this.game.collision.boxOverlaps(this.x - 1.9, this.y + 2 - 5, 3.8, 10);
  }
  /** Does Juni's physics box overlap a world-space circle (Switch CIRCLE shape)? */
  overlapsCircle(cx: number, cy: number, r: number): boolean {
    const bx = this.x + this.boxOffX;
    const by = this.y + BOX_OFF_Y;
    const nx = Math.max(bx - HALF_W, Math.min(cx, bx + HALF_W));
    const ny = Math.max(by - HALF_H, Math.min(cy, by + HALF_H));
    return (nx - cx) ** 2 + (ny - cy) ** 2 < r * r;
  }

  /** Does Juni's physics box overlap an arbitrary world rect? */
  overlapsRect(rx: number, ry: number, rw: number, rh: number): boolean {
    const l = this.x + this.boxOffX - HALF_W;
    const t = this.y + BOX_OFF_Y - HALF_H;
    const r = l + C.JUNI_WIDTH;
    const b = t + C.JUNI_HEIGHT;
    return l < rx + rw && r > rx && t < ry + rh && b > ry;
  }
  /**
   * The open umbrella's canopy StaticBody2D (Juni.tscn UmbrellaBody, collision layer
   * 4096): live from deploy until 0.2s after stow starts (Umbrella.cs — the shape only
   * disables on CloseTimer timeout, which matches the stow animation's length), and
   * never for a custom character's umbrella (Umbrella.Custom). Bullets whose Godot
   * collision_mask includes 4096 die against it.
   */
  get umbrellaBlocking(): boolean {
    return !this.dead && !this.umbrellaCustom && (this._umbrellaDeployed || this.umbrellaAnim === 'stow');
  }
  /**
   * Canopy polygon (-9,-5, -9,-11, -5,-15, 1,-15, 4,-13, 4,-11) local to UmbrellaBody
   * (Umbrella at (0,-3.95) + body at (0,4) → (0, 0.05) from the origin), mirrored with
   * facing. Approximated by its bbox (13×10 at y −14.9..−4.9) — the clipped corners of
   * the hexagon are within ~3px of it.
   */
  umbrellaBlocksRect(rx: number, ry: number, rw: number, rh: number): boolean {
    if (!this.umbrellaBlocking) return false;
    const l = this.x + (this.facingRight ? -9 : -4);
    const t = this.y + 0.05 - 15;
    return l < rx + rw && l + 13 > rx && t < ry + rh && t + 10 > ry;
  }
  /**
   * Juni's narrow BULLET hitbox (Juni.tscn's Hitbox Area2D, collision_mask = the Bullet
   * layer only — object/hazard areas detect the wider physics body instead): UsualShape
   * 6x16.6 at (0, 0.3); while climbing/sliding, ClimbShape 8x15 at (2, -0.4), mirrored
   * by facing (Juni.cs flips the whole body via Scale.x = -1).
   */
  overlapsBulletRect(rx: number, ry: number, rw: number, rh: number): boolean {
    let l: number, t: number, r: number, b: number;
    if (this.state.name === 'Climb' || this.state.name === 'Slide') {
      const cx = this.x + (this.facingRight ? 2 : -2);
      l = cx - 4;
      r = cx + 4;
      t = this.y - 0.4 - 7.5;
      b = this.y - 0.4 + 7.5;
    } else {
      l = this.x - 3;
      r = this.x + 3;
      t = this.y + 0.3 - 8.3;
      b = this.y + 0.3 + 8.3;
    }
    return l < rx + rw && r > rx && t < ry + rh && b > ry;
  }
  /**
   * Spring.cs: snap Juni to the spring top (+1px for 96px obstacles), then launch
   * through executeJump — the Jumped signal fires (SmartBouncers listen) and
   * justClimbed/canFreeJump clear, unlike a raw velocity poke. No jump sfx (the
   * spring plays its own "bounce").
   */
  springBounce(springTopY: number, speed: number): void {
    this.moveY(-(this.y + BOX_OFF_Y + HALF_H - springTopY + 1));
    this.grounded = false;
    this.executeJump(speed, false, true, false);
  }
  /** A checker Area2D strip at local (±px, py) with extents (ex, ey), mirrored by d. */
  private checkerStrip(d: number, px: number, py: number, ex: number, ey: number): boolean {
    return this.game.collision.boxOverlaps(this.x + d * px - ex, this.y + py - ey, ex * 2, ey * 2);
  }
  /** Climb checker (Juni.tscn): a 0.4×8.6 strip at (6.23, 0.2) — only mid-body grabs walls. */
  touchingWall(d: number): boolean {
    return this.checkerStrip(d, 6.23, 0.2, 0.2, 4.3);
  }
  /**
   * Checkers.cs Bump/LittleBump: solid in the foot strip (6.2, 6.5)±(0.2, 2) ahead, with
   * the climb strip clear (obstacle top ≤4px above the foot). LittleBump additionally
   * requires the BigBump strip (6.2, 5.5)±(0.2, 1) clear → obstacle ≤2px. Ground states
   * and pull-over step 4px obstacles; air states only 2px.
   */
  bump(little: boolean): boolean {
    const d = this.dir !== 0 ? this.dir : this.facingRight ? 1 : -1;
    if (!this.checkerStrip(d, 6.2, 6.5, 0.2, 2)) return false;
    if (this.checkerStrip(d, 6.23, 0.2, 0.2, 4.3)) return false;
    if (little && this.checkerStrip(d, 6.2, 5.5, 0.2, 1)) return false;
    return true;
  }

  private moveAndCollide(dt: number): void {
    this.pushOut();
    // X then Y, matching the two-step move in the original.
    this.moveX(this.vx * dt);
    this.moveY(this.vy * dt);
    this.updateGrounded();
  }

  /**
   * yknytt Juni.cs PUSH_OUT_PX(1)/PUSH_OUT_TRIES(5): if Juni starts a frame already
   * overlapping solid geometry (bad warp landing, a block stamped onto her, etc.),
   * nudge her away by 1px total over 5 sub-steps so she can escape instead of
   * staying wedged. Direction ≈ away from the contact: here, away from the centroid
   * of the solid pixels inside her AABB (the pixel-grid analogue of Godot's
   * collision Position).
   */
  private pushOut(): void {
    const PUSH_OUT_PX = 1;
    const PUSH_OUT_TRIES = 5;
    for (let i = 0; i < PUSH_OUT_TRIES; i++) {
      if (!this.overlapsAt(this.x, this.y)) return;
      let sx = 0;
      let sy = 0;
      let n = 0;
      const x0 = Math.floor(this.x + this.boxOffX - HALF_W);
      const y0 = Math.floor(this.y + BOX_OFF_Y - HALF_H);
      for (let py = 0; py < C.JUNI_HEIGHT; py++) {
        for (let px = 0; px < C.JUNI_WIDTH; px++) {
          if (this.game.collision.solidAt(x0 + px, y0 + py)) {
            sx += x0 + px + 0.5;
            sy += y0 + py + 0.5;
            n++;
          }
        }
      }
      if (n === 0) return;
      let bx = this.x - sx / n;
      let by = this.y - sy / n;
      const len = Math.hypot(bx, by);
      if (len < 1e-6) {
        bx = 0;
        by = -1;
      } else {
        bx /= len;
        by /= len;
      }
      this.x += (bx * PUSH_OUT_PX) / PUSH_OUT_TRIES;
      this.y += (by * PUSH_OUT_PX) / PUSH_OUT_TRIES;
    }
  }

  private moveX(dx: number): void {
    let remaining = dx;
    const step = Math.sign(dx);
    while (Math.abs(remaining) > 1e-4) {
      const d = Math.abs(remaining) < 1 ? remaining : step;
      if (!this.overlapsAt(this.x + d, this.y)) {
        this.x += d;
      } else {
        // try stepping up small obstacles / slopes
        let climbed = false;
        for (let u = 1; u <= STEP_UP; u++) {
          if (!this.overlapsAt(this.x + d, this.y - u) && !this.overlapsAt(this.x, this.y - u)) {
            this.x += d;
            this.y -= u;
            climbed = true;
            break;
          }
        }
        if (!climbed) {
          this.vx = 0;
          return;
        }
      }
      remaining -= d;
    }
  }

  private moveY(dy: number): void {
    let remaining = dy;
    const step = Math.sign(dy);
    while (Math.abs(remaining) > 1e-4) {
      const d = Math.abs(remaining) < 1 ? remaining : step;
      if (!this.overlapsAt(this.x, this.y + d)) {
        this.y += d;
      } else {
        if (d > 0) this.grounded = true;
        this.vy = 0;
        return;
      }
      remaining -= d;
    }
  }

  private updateGrounded(): void {
    if (this.overlapsAt(this.x, this.y + 1)) {
      this.grounded = true;
      return;
    }
    // snap to ground within GROUND_CONTACT_PX when descending/standing
    if (this.vy >= 0) {
      for (let s = 1; s <= C.GROUND_CONTACT_PX; s++) {
        if (this.overlapsAt(this.x, this.y + s)) {
          this.y += s - 1;
          this.grounded = true;
          return;
        }
      }
    }
    this.grounded = false;
  }

  // ---- Death ----
  die(): void {
    if (this.dead) return;
    this.dead = true;
    // Juni.cs die(): next_state = null; clearState() → the current state's onExit()
    // stops the Walk/Run/Climb/Slide loop players.
    this.nextState = null;
    this.state.onExit();
    this.game.audio.sfx('Into Smoke'); // yknytt death sound
    this.game.deathPoof.burst(this.x, this.y - 4); // poof of smoke particles
    this.game.onPlayerDied();
    this.syncSprite(); // hide immediately — update() bails out early while dead
  }

  respawn(): void {
    this.dead = false;
    this.justReset = 2; // Juni.cs reset(): 2-frame collision grace
    this.vx = 0;
    this.vy = 0;
    this.jumps = 0;
    this.justClimbed = false;
    this.canFreeJump = false;
    this._umbrellaDeployed = false; // reset without playing the stow sound
    this.umbrellaAnim = 'none';
    if (this.umbrellaSprite) this.umbrellaSprite.visible = false;
    this.stopHologram(true);
    this.swimZones = 0;
    this.noJumps = 0;
    this.stickies = 0;
    this.updrafts = 0;
    this.noClimbZones = 0;
    if (this.muffleZones > 0) this.game.audio.setMovementMuffle(false);
    this.muffleZones = 0;
    this.state.onExit(); // exit the pre-death state (idempotent after die())
    this.nextState = null;
    this.state = new IdleState(this);
    this.state.onEnter();
  }

  // ---- Animation / render ----
  playAnim(name: string): void {
    if (this.animName === name) return;
    this.animName = name;
    this.animTime = 0;
    this.animIndex = 0;
  }
  continueFall(): void {
    this.playAnim('Falling');
  }

  private animate(dt: number): void {
    const def = ANIMS[this.animName] ?? ANIMS.Idle;
    this.animTime += dt;
    if (this.animTime >= def.step) {
      this.animTime -= def.step;
      this.animIndex++;
      if (this.animIndex >= def.frames.length) {
        if (def.loop) this.animIndex = 0;
        else {
          this.animIndex = def.frames.length - 1;
          if (def.next) {
            this.playAnim(def.next);
            return;
          }
        }
      }
    }
    const frame = def.frames[this.animIndex];
    const tex = this.frameTextures[frame];
    if (tex) this.sprite.texture = tex;
  }

  private syncSprite(): void {
    this.container.x = Math.round(this.x);
    this.container.y = Math.round(this.y);
    this.sprite.scale.x = this.facingRight ? 1 : -1;
    // Juni fades to 45% while a hologram decoy is out (yknytt).
    this.container.alpha = this.dead ? 0 : this.hologramActive ? 0.45 : 1;
    if (this.shiftHintSprite) {
      const show = (this.onShift > 0 || this.onPlatform > 0) && !this.dead;
      this.shiftHintSprite.visible = show;
      // showShiftHint's jump_hint recolour (light 0.75,1,0.75) for platforms.
      if (show) this.shiftHintSprite.tint = this.onPlatform > 0 ? 0xbfffbf : 0xffffff;
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
